import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import type { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { LojaDataGrid } from "../../../common";
import type {
  ContaReceberStatusTipo,
  ContaReceberView,
} from "../types/contas-receber.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";

type Row = ContaReceberView;

function chipColor(
  tipo: ContaReceberStatusTipo
): "success" | "error" | "warning" | "default" {
  switch (tipo) {
    case "pago":
      return "success";
    case "vencido":
      return "error";
    case "pendente":
      return "warning";
    default:
      return "default";
  }
}

type Props = {
  linhas: ContaReceberView[];
  total: number;
  page: number;
  limit: number;
  loading?: boolean;
  onPaginationModelChange: (m: GridPaginationModel) => void;
  onBaixar?: (row: ContaReceberView) => void;
  onCancelar?: (row: ContaReceberView) => void;
};

function podeExibirBaixaReceber(row: ContaReceberView): boolean {
  if (row.valorAberto <= 0) return false;
  if (row.statusTipo === "pago" || row.statusTipo === "cancelado") return false;
  return true;
}

function podeExibirCancelarReceber(row: ContaReceberView): boolean {
  if (row.statusTipo === "neutro") return false;
  if (row.statusTipo === "pago" || row.statusTipo === "cancelado") return false;
  if (row.valorAberto <= 0) return false;
  return row.statusTipo === "pendente" || row.statusTipo === "vencido";
}

export default function ContasReceberTabela({
  linhas,
  total,
  page,
  limit,
  loading,
  onPaginationModelChange,
  onBaixar,
  onCancelar,
}: Props) {
  const theme = useTheme();

  const rows: Row[] = linhas.map((r) => ({ ...r, id: r.id }));

  const columns: GridColDef<Row>[] = [
    {
      field: "dataVencimentoLabel",
      headerName: "Vencimento",
      width: 120,
      renderCell: (p) => {
        const dest = p.row.vencido && p.row.statusTipo !== "pago";
        return (
          <Typography
            variant="body2"
            sx={{
              py: 0.5,
              fontWeight: dest ? 700 : 400,
              color: dest ? theme.palette.error.main : undefined,
            }}
          >
            {p.row.dataVencimentoLabel}
            {p.row.diasAtraso > 0 && p.row.vencido ? (
              <Typography
                component="span"
                variant="caption"
                display="block"
                color="error"
              >
                {p.row.diasAtraso}d atraso
              </Typography>
            ) : null}
          </Typography>
        );
      },
    },
    {
      field: "clienteNome",
      headerName: "Cliente",
      flex: 1,
      minWidth: 160,
      renderCell: (p) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            title={p.row.clienteNome}
          >
            {p.row.clienteNome}
          </Typography>
          {p.row.clienteEmail ? (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
            >
              {p.row.clienteEmail}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      field: "pedidoReferencia",
      headerName: "Pedido",
      width: 130,
      renderCell: (p) => (
        <Typography
          variant="body2"
          sx={{ py: 0.5, fontFamily: p.row.pedidoReferencia ? "inherit" : "monospace" }}
          title={p.row.pedidoId ?? undefined}
        >
          {p.row.pedidoReferencia ??
            (p.row.pedidoId ? p.row.pedidoId.slice(0, 8) : "—")}
        </Typography>
      ),
    },
    {
      field: "origemLabel",
      headerName: "Origem",
      width: 140,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }}>
          {p.row.origemLabel}
        </Typography>
      ),
    },
    {
      field: "centroCustoNome",
      headerName: "Centro de custo",
      flex: 0.9,
      minWidth: 130,
      renderCell: (p) => (
        <Typography
          variant="body2"
          sx={{
            py: 0.5,
            fontWeight: p.row.semCentro ? 600 : 400,
            color: p.row.semCentro ? theme.palette.warning.dark : undefined,
          }}
        >
          {p.row.centroCustoNome}
        </Typography>
      ),
    },
    {
      field: "valorBruto",
      headerName: "Valor bruto",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }}>
          {formatarMoedaBRL(p.row.valorBruto)}
        </Typography>
      ),
    },
    {
      field: "valorLiquido",
      headerName: "Valor líquido",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }}>
          {formatarMoedaBRL(p.row.valorLiquido)}
        </Typography>
      ),
    },
    {
      field: "valorAberto",
      headerName: "Valor aberto",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            py: 0.5,
            color:
              p.row.valorAberto > 0 && p.row.vencido
                ? theme.palette.error.main
                : undefined,
          }}
        >
          {formatarMoedaBRL(p.row.valorAberto)}
        </Typography>
      ),
    },
    {
      field: "statusLabel",
      headerName: "Status",
      width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.statusLabel}
          color={chipColor(p.row.statusTipo)}
          variant={p.row.statusTipo === "pendente" ? "filled" : "outlined"}
          sx={{ my: 0.5 }}
        />
      ),
    },
    {
      field: "pagoEmLabel",
      headerName: "Pago em",
      width: 110,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ py: 0.5 }}>
          {p.row.pagoEmLabel}
        </Typography>
      ),
    },
    ...(onBaixar || onCancelar
      ? [
          {
            field: "acoes",
            headerName: "Ações",
            sortable: false,
            filterable: false,
            width: onBaixar && onCancelar ? 208 : 112,
            renderCell: (p: { row: Row }) => (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ py: 0.25 }}
              >
                {onBaixar ? (
                  <MDButton
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ py: 0.25, px: 1, minHeight: 30 }}
                    disabled={
                      !podeExibirBaixaReceber(p.row) || Boolean(loading)
                    }
                    onClick={() => onBaixar(p.row)}
                  >
                    Baixar
                  </MDButton>
                ) : null}
                {onCancelar ? (
                  <MDButton
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ py: 0.25, px: 1, minHeight: 30 }}
                    disabled={
                      !podeExibirCancelarReceber(p.row) || Boolean(loading)
                    }
                    onClick={() => onCancelar(p.row)}
                  >
                    Cancelar
                  </MDButton>
                ) : null}
              </Stack>
            ),
          } as GridColDef<Row>,
        ]
      : []),
  ];

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
          >
            Títulos a receber
          </Typography>
        </Box>
        <Box sx={{ px: 2, pb: 2, width: "100%", overflowX: "auto" }}>
          <LojaDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            paginationModel={{
              page: Math.max(0, page - 1),
              pageSize: limit,
            }}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            disableColumnMenu
            columnHeaderHeight={44}
            rowHeight={52}
            getRowClassName={(params) =>
              params.row.vencido && params.row.statusTipo !== "pago"
                ? "cr-row-vencido"
                : ""
            }
            sx={{
              minWidth:
                onBaixar && onCancelar
                  ? 1188
                  : onBaixar || onCancelar
                    ? 1128
                    : 980,
              "& .cr-row-vencido": {
                backgroundColor: alpha(theme.palette.error.main, 0.06),
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
